import { z } from 'zod'
import type { Lang } from './content'

/**
 * What language a BOOK is, as against what language a run of text is.
 *
 * These are two different facts and the whole module exists to keep them apart.
 * A run's language decides which face sets that phrase — `content.ts` owns
 * that, and nothing here touches it. A book's language decides which way the
 * object is bound, and therefore which physical page a chapter opens on, which
 * edge the spine eats, and which language the chrome around the text speaks.
 *
 * Two questions follow from it, and they have different answers:
 *
 *   - **Which language LEADS** — the primary. Binding, running heads, the
 *     contents, the folios: all of it belongs to one language, because a book
 *     is bound one way.
 *   - **Whether Hebrew is present at all** — either side of the pair. What the
 *     running text is set in, and whether the book needs Hebrew type at all,
 *     turn on this and not on which one leads.
 *
 * Asking the first where the second is meant is how an English-led bilingual
 * book comes out with no Hebrew face in it.
 */

export const bookLanguageSchema = z.enum(['en', 'he'])
export type BookLanguage = z.infer<typeof bookLanguageSchema>

/**
 * A book's languages: the one it leads in, and the one set beside it.
 *
 * A pair rather than a three-valued enum, because "English/Hebrew" is not a
 * third language — it is two, one of which leads. An enum would have had to
 * spell out which led as part of its own name, and every question asked of it
 * would then be a string comparison against a compound.
 */
export type BookLanguages = {
  primary: BookLanguage
  /** The language set beside the primary. Null is a single-language book. */
  secondary: BookLanguage | null
}

export const ENGLISH: BookLanguages = { primary: 'en', secondary: null }

/** Which way the book is bound, and therefore which way its pages progress. */
export const bookDirection = (book: BookLanguages): 'ltr' | 'rtl' =>
  book.primary === 'he' ? 'rtl' : 'ltr'

/**
 * Whether the book carries Hebrew anywhere, in either role.
 *
 * The test for type, never for direction: a book that leads in English and
 * carries Hebrew beside it binds left-to-right and still needs a Hebrew face
 * for its running text.
 */
export const hasHebrew = (book: BookLanguages): boolean =>
  book.primary === 'he' || book.secondary === 'he'

/** Whether both languages are set — one flow carrying the two of them. */
export const isBilingual = (book: BookLanguages): boolean =>
  book.secondary !== null && book.secondary !== book.primary

/**
 * How the numbers on the page are written.
 *
 * A Hebrew book folios in Hebrew letters, and it is a separate setting because
 * a modern Hebrew book may well use figures — so the language decides the
 * DEFAULT and never the value. `page-folios` names the CSS counter style, which
 * is what the printed page ultimately reads.
 */
export const folioStyleSchema = z.enum(['arabic', 'hebrew'])
export type FolioStyle = z.infer<typeof folioStyleSchema>

export const defaultFolios = (book: BookLanguages): FolioStyle =>
  book.primary === 'he' ? 'hebrew' : 'arabic'

/**
 * What a book's running text is set in.
 *
 * Both faces carry Hebrew and Latin from one drawing, which is what qualifies
 * either: this material is bilingual to the sentence, so a face that borrowed
 * the other script would set single lines in two type classes. It is the BOOK's
 * and reaches the printed page — how large the text is set is the reader's, and
 * does not.
 */
export const bookTypefaceSchema = z.enum(['sans', 'serif'])
export type BookTypeface = z.infer<typeof bookTypefaceSchema>

/** The CSS counter style a folio setting names. */
export const folioCounterStyle = (style: FolioStyle): string =>
  style === 'hebrew' ? 'hebrew' : 'decimal'

/**
 * The words the book says in its own voice — a contents page, a list of notes.
 *
 * They belong to the PRIMARY language and not to the runs around them: this is
 * the book speaking about itself, so it speaks one language however many the
 * text carries. It is a fixed pair rather than a translation layer because
 * these are the only two words the renderer writes.
 */
const CHROME = {
  en: { contents: 'Contents', notes: 'Notes' },
  he: { contents: 'תוכן העניינים', notes: 'הערות' },
} as const

export const bookChrome = (book: BookLanguages) => CHROME[book.primary]

/**
 * Which family the book's RUNNING TEXT is set in.
 *
 * `latin` is a serif book with Hebrew phrases inside it; `hebrew` is a book
 * whose body is Hebrew. Decided by what leads, because that is what most of the
 * words are — a run of the other language still takes its own face from its own
 * `lang`, which is the layer underneath this one and is not overridden by it.
 */
export const bookFace = (book: BookLanguages): 'latin' | 'hebrew' =>
  book.primary === 'he' ? 'hebrew' : 'latin'

/**
 * What language a run is actually in.
 *
 * An unmarked run is the BOOK's language, which is the only reading that
 * survives a book being Hebrew: an author writing a Hebrew manuscript does not
 * tag every paragraph, and treating silence as English would set the whole book
 * in a Latin face and run it the wrong way.
 */
export const runLanguage = (
  book: BookLanguages,
  lang: Lang | undefined,
): Lang => lang ?? book.primary

/**
 * The direction a run has to state for itself, or nothing where it agrees with
 * the book.
 *
 * Only the runs going the OTHER way are marked. A run left to the bidi
 * algorithm's own devices takes its neighbors' punctuation with it — an
 * English clause inside a Hebrew paragraph ending up with its full stop at the
 * wrong end — and marking it is also what isolates it, since `dir` on an inline
 * element carries `unicode-bidi: isolate`.
 *
 * A mixed run states nothing: it is deliberately both, so it belongs to the
 * paragraph running around it.
 */
export const runDirection = (
  book: BookLanguages,
  lang: Lang | undefined,
): 'ltr' | 'rtl' | undefined => {
  const effective = runLanguage(book, lang)
  if (effective === 'mixed') return undefined
  const dir = effective === 'he' || effective === 'aramaic' ? 'rtl' : 'ltr'
  return dir === bookDirection(book) ? undefined : dir
}

/**
 * Everything a run's language decides about the element it is set in.
 *
 * One answer, for both renderers. The page writes it as an attribute string and
 * the screen spreads it onto a React element, which is a difference of
 * transport rather than of meaning.
 *
 * `lang` is 'he' for a Hebrew-script run, Aramaic included: the attribute names
 * a SCRIPT to a reader that is about to speak it, and no browser or screen
 * reader distinguishes the two. What tells them apart is the field.
 *
 * A mixed run names no language, for the reason it states no direction: it is
 * deliberately both scripts, so calling it Hebrew hands a screen reader the
 * English half to say in Hebrew. Its face comes from `book-mixed`, which is a
 * class and not a claim about what the words are.
 *
 * What the words INSIDE a run say for themselves is `runSegments`, and the two
 * are one statement: this answers for the element, that for the stretches in
 * it.
 */
export const runAttributes = (
  book: BookLanguages,
  lang: Lang | undefined,
): { lang?: 'he'; dir?: 'ltr' | 'rtl'; className?: 'book-mixed' } => {
  const effective = runLanguage(book, lang)
  const dir = runDirection(book, lang)
  return {
    ...(effective === 'he' || effective === 'aramaic'
      ? { lang: 'he' as const }
      : {}),
    ...(dir ? { dir } : {}),
    ...(effective === 'mixed' ? { className: 'book-mixed' as const } : {}),
  }
}

/**
 * One stretch of a run's words, and what it states for itself.
 *
 * A stretch stating no `lang` is read in the language of whatever encloses it —
 * the run, and the book around that.
 */
export type RunSegment = { text: string; lang?: 'he' | 'en' }

/** Which script a stretch of words is written in, where it is written in one. */
type Script = 'he' | 'latin' | null

/*
 * By SCRIPT rather than by the block U+0590–U+05FF, which is not the same set.
 * On the block's own contents the two agree — every character assigned in it is
 * of the Hebrew script, pointing and maqaf and sof pasuq alike — and they part
 * outside it: the presentation forms at U+FB1D–U+FB4F are of the Hebrew script
 * and no Hebrew range holds them, so a precomposed בּ or an alternative ﬠ,
 * which some sources deliver in place of a letter and the point on it, reads to
 * a range as of no script at all and is handed to whatever surrounds it. A
 * range also matches the code points the block reserves and nothing has been
 * assigned to, answering for characters that do not exist.
 */
const HEBREW_SCRIPT = /\p{Script=Hebrew}/u
const LATIN_SCRIPT = /\p{Script=Latin}/u

/*
 * Read by GRAPHEME CLUSTER, so a boundary can never fall between a letter and
 * the marks on it. The marks carrying no script of their own are what turns on
 * this — a combining acute is Inherited, neither Hebrew nor Latin — because
 * read one code point at a time such a mark parts from its letter and is
 * settled by whatever sits beside it: an `é` ending the Latin where the Hebrew
 * begins comes out as an `e` whose accent is read as Hebrew. Hebrew pointing is
 * of the Hebrew script itself and divides the same either way.
 */
const CLUSTERS = new Intl.Segmenter(undefined, { granularity: 'grapheme' })

/**
 * Whether these words are of two scripts.
 *
 * One answer to that question, because more than the division below asks it: a
 * caller weighing what somebody marked as a pairing is asking exactly this, and
 * two spellings of it would be two rules about the same runs.
 */
export const holdsBothScripts = (text: string): boolean =>
  HEBREW_SCRIPT.test(text) && LATIN_SCRIPT.test(text)

const scriptOf = (cluster: string): Script =>
  HEBREW_SCRIPT.test(cluster)
    ? 'he'
    : LATIN_SCRIPT.test(cluster)
      ? 'latin'
      : null

/**
 * How a run's words divide by SCRIPT, where nobody stated a language for them.
 *
 * A run that states one is answered by that statement alone and this returns
 * null: `lang` decides which sentence a neutral belongs to, which no rule over
 * characters can recover, and the author's span boundary IS that decision. So
 * is a wholly one-script run, which already says everything it has to say.
 *
 * What is left is the run nobody marked that genuinely holds both scripts, and
 * the letters are evidence of exactly one thing about it: which script each
 * stretch is in. A stretch says that much and nothing else, so the Latin half
 * of it is not spoken in Hebrew and the Hebrew half is not left with no
 * language for a voice, a hyphenation or a fallback to read.
 *
 * **The `lang` attribute, and never a direction.** No bidi rule reads `lang`,
 * which is what makes this inert to the order the words come out in — and
 * inert is the requirement, because the order is already right. UAX #9 hands
 * the neutrals at a seam to the direction embedding them, so an unmarked
 * mixed-script sentence lays out correctly as it stands; a rule reassigning
 * them moves 8 of 16 realistic sentences and worsens seven, splitting a bracket
 * pair away from what it encloses and tearing a chapter number off its
 * reference. Two isolates in one paragraph are laid out relative to each other
 * in the PARAGRAPH's direction, so isolating both halves of a phrase reverses
 * them.
 *
 * A neutral stretch — a space, a bracket, a figure — therefore takes the script
 * on either side of it where the two agree, and takes its one neighbor where
 * it sits at an end. At a SEAM it takes neither: that is the neutral whose
 * ownership is contested, and it is left to be read in the language around it,
 * which is the answer the bidi algorithm gives for the same character.
 *
 * A stretch states its script only where that differs from what the run already
 * says, for the reason a run states a direction only where it goes the other
 * way from its book: an attribute repeating what encloses it says nothing and
 * is one more thing to keep true.
 */
export const runSegments = (
  book: BookLanguages,
  lang: Lang | undefined,
  text: string,
): RunSegment[] | null => {
  if (lang !== undefined) return null
  /* The cheap test first, and it is the one that answers for nearly every run
     in a book: a run of one script divides into itself. */
  if (!holdsBothScripts(text)) return null

  const stretches: { text: string; script: Script }[] = []
  for (const { segment } of CLUSTERS.segment(text)) {
    const script = scriptOf(segment)
    const last = stretches[stretches.length - 1]
    if (last && last.script === script) last.text += segment
    else stretches.push({ text: segment, script })
  }

  /* The merge above opens a stretch only where the script CHANGES, so no two in
     a row share one and a neutral never has a neutral beside it. Nothing this
     loop settles is read by it. */
  for (const [i, stretch] of stretches.entries()) {
    if (stretch.script) continue
    const before = stretches[i - 1]?.script ?? null
    const after = stretches[i + 1]?.script ?? null
    if (before && after) {
      if (before === after) stretch.script = before
      continue
    }
    stretch.script = before ?? after
  }

  const effective = runLanguage(book, lang)
  const stated = (script: Script): 'he' | 'en' | undefined =>
    script === 'he'
      ? effective === 'he'
        ? undefined
        : 'he'
      : script === 'latin'
        ? effective === 'en'
          ? undefined
          : 'en'
        : undefined

  const out: RunSegment[] = []
  for (const stretch of stretches) {
    const tag = stated(stretch.script)
    const last = out[out.length - 1]
    if (last && last.lang === tag) last.text += stretch.text
    else
      out.push(tag ? { text: stretch.text, lang: tag } : { text: stretch.text })
  }
  return out
}
